import React from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col, Button, Form, Alert } from 'reactstrap';
import Loader from '../../Layout/Loader';
import { Navigate } from 'react-router-dom';
import withRouter from '../../Routing/withRouter';

import { withMobileAppMode } from '../../Layout';

import InputPassword from '../../FormElements/InputPasswordControlled';
import { validateField } from '../../Validation/validateField';
import { validateFields } from '../../Validation/validateFields';
import RequiredRule from '../../Validation/ValidationRules/RequiredRule';
import ValidationErrors from '../../Validation/ValidationErrors';
import { withTranslator } from '../../I18n/withTranslator';
import { withToast } from '../../Toast';

export class AuthResetPasswordComponent extends React.Component {
  static propTypes = {
    router: PropTypes.shape({
      params: PropTypes.shape({
        token: PropTypes.string,
      }).isRequired,
    }).isRequired,
    resetPassword: PropTypes.func.isRequired,
    validateToken: PropTypes.func.isRequired,
    mobileAppMode: PropTypes.bool.isRequired,
    t: PropTypes.func.isRequired,
    addMessage: PropTypes.func.isRequired,
    toastError: PropTypes.func.isRequired,
    toastSuccess: PropTypes.func.isRequired,
  };

  state = {
    password: '',
    preload: false,
    tokenError: false,
    error: false,
    success: false,
    errors: new ValidationErrors(),
  };

  validationRules = {
    password: [new RequiredRule({ translator: this.props.t })],
  };

  componentDidMount = async () => {
    const token = this.props.router.params.token;
    const response = await this.props.validateToken({ token });
    const { validateResetPasswordToken } = response.data.auth;

    try {
      if (!validateResetPasswordToken) {
        this.props.toastError(
          <p className="mb-0">
            {this.props.t('auth/reset-password/token-is-not-valid-error-message')}
          </p>
        );

        this.setState({ tokenError: true });
      }
    } catch (e) {
      this.props.toastError();
      throw new Error(`Failed to validate reset password token, got error: ${e}`);
    }
  };

  handleInputChange = (event) => {
    this.setState({ [event.target.name]: event.target.value });
    this.validateInput(event.target.name, event.target.value);
  };

  validateInput = (field, value) => {
    if (this.validationRules[field]) {
      this.setState((prevState) => ({
        errors: {
          ...prevState.errors,
          details: validateField(field, value, this.validationRules[field], prevState),
        },
      }));
    }
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const frontEndErrors = validateFields(this.validationRules, this.state, this.props.t);

    this.setState((prevState) => ({
      errors: {
        ...prevState.errors,
        details: frontEndErrors,
      },
    }));

    if (frontEndErrors.length === 0) {
      this.setState({ preload: true });

      try {
        const response = await this.props.resetPassword({
          token: this.props.router.params.token,
          password: this.state.password,
        });
        const { code, details } = response.data.auth.resetPassword;

        if (code === 200) {
          this.setState({
            password: '',
            error: false,
            errors: new ValidationErrors(),
            success: true,
            preload: false,
          });

          this.props.toastSuccess(
            <p className="mb-0">{this.props.t('auth/reset-password/success-message')}</p>
          );
        } else {
          for (let i = 0; i < details.length; i += 1) {
            const error = details[i];
            if (error.fieldName in this.validationRules) {
              this.setState({
                errors: response.data.auth.resetPassword,
              });
              break;
            }
          }
          this.setState({
            error: true,
            success: false,
            preload: false,
          });
        }
      } catch (e) {
        this.props.toastError();
        this.setState({
          success: false,
          preload: false,
        });
        throw new Error(`Failed to set new password, got error: ${e}`);
      }
    }
  };

  render() {
    if (!this.props.mobileAppMode && this.state.success) {
      return <Navigate to="/auth/login" />;
    }

    if (this.state.tokenError) {
      return <Navigate to="/auth/login" />;
    }

    return (
      <>
        <section className="bg-white d-flex align-items-center full-height">
          <Container className="text-center">
            <h1 className="title-medium semi-bold pt-4">
              {this.props.t('auth/reset-password/title')}
            </h1>
            <Row>
              <Col xs="12" sm={{ size: 6, offset: 3 }}>
                {this.state.error ? (
                  <Alert color="danger">{this.props.t('auth/reset-password/error-message')}</Alert>
                ) : (
                  ''
                )}
                <Form
                  onSubmit={this.handleSubmit}
                  data-test="reset-password-form"
                  className="text-start"
                >
                  <InputPassword
                    label={this.props.t('password')}
                    name="password"
                    value={this.state.password}
                    errors={this.state.errors}
                    handleChange={this.handleInputChange}
                    validationRules={this.validationRules.password}
                    showAsterisk={false}
                    data-test="password-input"
                  />
                  <Button color="primary" data-test="reset-password-button" className="w-100">
                    {this.props.t('save')}
                  </Button>
                </Form>
              </Col>
            </Row>
          </Container>
        </section>
        {this.state.preload ? <Loader /> : ''}
      </>
    );
  }
}

export default withMobileAppMode(withRouter(withTranslator(withToast(AuthResetPasswordComponent))));
